<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg viewBox="-200 -200 400 400" class="canvas-svg">
          <!-- The four intersecting lines forming the complete quad -->
          <line x1="-200" y1="50" x2="200" y2="50" stroke="#334155" stroke-width="2" /> <!-- L1 -->
          <line x1="-150" y1="-150" x2="100" y2="200" stroke="#334155" stroke-width="2" /> <!-- L2 -->
          <line x1="150" y1="-150" x2="-100" y2="200" stroke="#334155" stroke-width="2" /> <!-- L3 -->
          <line x1="-200" y1="y4Left" :x2="200" :y2="y4Right" stroke="#334155" stroke-width="2" /> <!-- L4 -->

          <!-- Vertices -->
          <circle v-for="(pt, index) in vertices" :key="'v'+index" :cx="pt.x" :cy="pt.y" r="4" fill="#64748b" />

          <!-- Diagonals -->
          <g v-if="isValid">
            <line :x1="vertices[0].x" :y1="vertices[0].y" :x2="vertices[3].x" :y2="vertices[3].y" stroke="#0D68FF" stroke-width="1.5" stroke-dasharray="3,3" />
            <line :x1="vertices[1].x" :y1="vertices[1].y" :x2="vertices[2].x" :y2="vertices[2].y" stroke="#0D68FF" stroke-width="1.5" stroke-dasharray="3,3" />
            <line :x1="vertices[4].x" :y1="vertices[4].y" :x2="vertices[5].x" :y2="vertices[5].y" stroke="#0D68FF" stroke-width="1.5" stroke-dasharray="3,3" />
          </g>

          <!-- Midpoints of Diagonals -->
          <g v-if="isValid">
            <circle :cx="m1.x" :cy="m1.y" r="5" fill="#facc15" />
            <circle :cx="m2.x" :cy="m2.y" r="5" fill="#facc15" />
            <circle :cx="m3.x" :cy="m3.y" r="5" fill="#facc15" />
            
            <!-- Newton Line -->
            <line :x1="m1.x - (m3.x - m1.x)*2" :y1="m1.y - (m3.y - m1.y)*2" 
                  :x2="m3.x + (m3.x - m1.x)*2" :y2="m3.y + (m3.y - m1.y)*2" 
                  stroke="#facc15" stroke-width="2" />
          </g>
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             完全四边形: 4条线 & 6个交点
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item" style="grid-column: span 2;">
                     <label>旋转控制切线角度</label>
                     <el-slider v-model="angleL4" :min="-45" :max="45" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>平移切线高度</label>
                     <el-slider v-model="offsetL4" :min="-80" :max="30" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>概念与牛顿线：</strong>四条两两相交且无三线共点的直线构成“完全四边形”，共有6个项点。将这6个顶点中不共用同一条直线的对点连接构成3条对角线。不管图形怎么变，3条对角线的中点必然处于同一直线（即牛顿线）上。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'CompleteQuadrilateralViz',
  data() {
    return {
      angleL4: 0,
      offsetL4: -50,
      L1: { a: 0, b: 1, c: -50 }, // y = 50 -> 0x + 1y - 50 = 0
      L2: { a: 1.4, b: -1, c: -60 }, // approx for x1=-150 y1=-150 to x2=100 y2=200
      L3: { a: 1.4, b: 1, c: -60 }
    }
  },
  computed: {
    y4Left() { return this.offsetL4 + Math.tan(this.angleL4 * Math.PI/180) * (-200); },
    y4Right() { return this.offsetL4 + Math.tan(this.angleL4 * Math.PI/180) * (200); },
    L4() {
      let slope = Math.tan(this.angleL4 * Math.PI/180);
      return { a: -slope, b: 1, c: -this.offsetL4 }; // -slope*x + y - offset = 0
    },
    vertices() {
      let lines = [this.L1, this.L2, this.L3, this.L4];
      let pts = [];
      // Intersections: 12, 13, 14, 23, 24, 34
      let pairs = [[0,1], [0,2], [0,3], [1,2], [1,3], [2,3]];
      pairs.forEach(p => {
        let i = this.intersect(lines[p[0]], lines[p[1]]);
        if (i) pts.push(i);
      });
      return pts;
    },
    isValid() {
      return this.vertices.length === 6;
    },
    m1() { return this.midpoint(this.vertices[0], this.vertices[5]); }, // L1L2 & L3L4
    m2() { return this.midpoint(this.vertices[1], this.vertices[4]); }, // L1L3 & L2L4
    m3() { return this.midpoint(this.vertices[2], this.vertices[3]); }  // L1L4 & L2L3
  },
  methods: {
    intersect(l1, l2) {
      let det = l1.a * l2.b - l2.a * l1.b;
      if (Math.abs(det) < 0.001) return null;
      let x = (l1.b * l2.c - l2.b * l1.c) / det;
      let y = (l2.a * l1.c - l1.a * l2.c) / det;
      return { x, y };
    },
    midpoint(p1, p2) {
      return { x: (p1.x + p2.x)/2, y: (p1.y + p2.y)/2 };
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { 
  flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .canvas-svg { width: 100%; height: 100%; }
  .hud-center {
    position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center;
    .status-badge {
      background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px);
      &.con { background: rgba(250,204,21,0.2); border-color: #facc15; color: #fde047; }
    }
  }
}
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
